import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CreditCard, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Pager from "@/components/Pager";
import { useAuth } from "@/context/AuthContext";
import { cad, usePricing } from "@/lib/pricing";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Manual legacy-tier overrides. Only approved paid plans are offered; "growth" is shown as Pro.
const TIER_CHOICES = [["starter", "Starter"], ["growth", "Pro"]];
const LEGACY_LABEL = { starter: "Starter", growth: "Pro", scale: "Scale (retired)" };

function TierDialog({ editing, setEditing, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = reason.trim().length >= 3 && !!editing?.tier;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/subscriptions/${editing.id}/tier`, { tier: editing.tier, reason: reason.trim() });
      toast.success(`Legacy tier set to ${LEGACY_LABEL[editing.tier]}`);
      setReason("");
      setEditing(null);
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Tier change failed — super admin only");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && !busy && setEditing(null)}>
      <DialogContent className="max-w-md" data-testid="tier-change-modal">
        <form onSubmit={save} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Change legacy tier · {editing?.name}</DialogTitle>
            <DialogDescription>Manual override for comps and corrections. It does not change the merchant's Stripe subscription or the plan used for Performance Fees. The change is audit-logged.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Legacy tier">
            {TIER_CHOICES.map(([t, label]) => (
              <button key={t} type="button" role="radio" aria-checked={editing?.tier === t} onClick={() => setEditing({ ...editing, tier: t })}
                className={`p-3 rounded-xl text-sm font-semibold transition-colors ${editing?.tier === t ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid={`tier-choice-${t}`}>{label}</button>
            ))}
          </div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Reason (required, audit log)
            <input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} maxLength={300}
              className="pf-input w-full px-3 py-2 mt-1 text-sm normal-case tracking-normal font-normal" placeholder="e.g. beta comp, downgrade for non-payment" data-testid="tier-reason" />
          </label>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setEditing(null)} disabled={busy} className="pf-btn-glass px-4 py-2 rounded-full text-sm" data-testid="tier-cancel">Cancel</button>
            <button type="submit" disabled={!ok || busy} className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="tier-save">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Saving…" : "Save"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminSubscriptions() {
  const { user } = useAuth();
  const sa = user?.role === "super_admin";
  const final = !!usePricing()?.final_active;
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1, page_size: 25 });
  const [editing, setEditing] = useState(null);

  const load = (p = page) => api.get(`/admin/subscriptions?page=${p}&page_size=25`).then((r) => {
    setItems(r.data.items || []);
    setMeta({ total: r.data.total, total_pages: r.data.total_pages, page_size: r.data.page_size });
    setPage(r.data.page);
  });
  useEffect(() => { load(1); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-subscriptions">
      <div className="pf-chip pf-chip-lav"><CreditCard className="w-3 h-3" /> Billing</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Subscriptions</h2>
      <p className="text-sm text-slate-600 mt-1">Every merchant store — current plan, active offers, invoices and lifetime subscription revenue.</p>

      <div className="pf-card overflow-x-auto mt-6">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-white/60 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-3">Store</th>
              <th className="px-5 py-3">Owner</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Active offers</th>
              <th className="px-5 py-3">Invoices</th>
              <th className="px-5 py-3">Lifetime</th>
              <th className="px-5 py-3"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-white/60" data-testid={`sub-row-${s.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{s.name}</td>
                <td className="px-5 py-3 text-slate-700 text-xs">{s.owner_email}</td>
                <td className="px-5 py-3" data-testid={`sub-plan-${s.id}`}>
                  <span className="pf-chip pf-chip-cyan">{final ? s.plan_label : LEGACY_LABEL[s.tier] || "Starter"}</span>
                  {final && s.tier && <span className="block text-[10px] text-slate-400 mt-1">Legacy tier: {LEGACY_LABEL[s.tier] || s.tier}</span>}
                </td>
                <td className="px-5 py-3 font-mono">{s.active_campaigns}</td>
                <td className="px-5 py-3 font-mono">{s.invoice_count}</td>
                <td className="px-5 py-3 font-mono" data-testid={`sub-lifetime-${s.id}`}>{s.invoice_count ? cad(s.lifetime_revenue || 0) : "—"}</td>
                <td className="px-5 py-3 text-right">
                  {sa && (
                    <button onClick={() => setEditing({ id: s.id, name: s.name, tier: TIER_CHOICES.some(([t]) => t === s.tier) ? s.tier : null })}
                      className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 whitespace-nowrap" data-testid={`sub-edit-tier-${s.id}`}>
                      <ArrowUpRight className="w-3 h-3" /> Legacy tier
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Pager page={page} totalPages={meta.total_pages} total={meta.total} pageSize={meta.page_size} onPage={load} testidPrefix="sub-pager" />
      <TierDialog editing={editing} setEditing={setEditing} onSaved={() => load(page)} />
    </div>
  );
}
